export const PERMISSION_TYPE_ENUM = {
  ORG: 'org',
  TEAM: 'team',
} as const;
export type PermissionTypeEnum = typeof PERMISSION_TYPE_ENUM[keyof typeof PERMISSION_TYPE_ENUM];

export const USER_PERMISSION_ROLE_ENUM = {
  ADMIN: 3,
  VIEWER: 1,
  NO_ACCESS: 0,
} as const;

export const PERMISSION_ROLES_ORDERED = [USER_PERMISSION_ROLE_ENUM.NO_ACCESS, USER_PERMISSION_ROLE_ENUM.VIEWER, USER_PERMISSION_ROLE_ENUM.ADMIN];

export const USER_ROLE_TRANSLATION_KEYS = {
  [USER_PERMISSION_ROLE_ENUM.ADMIN]: 'userPermissionRoles.admin',
  [USER_PERMISSION_ROLE_ENUM.VIEWER]: 'userPermissionRoles.viewer',
  [USER_PERMISSION_ROLE_ENUM.NO_ACCESS]: 'userPermissionRoles.noAccess',
} as const;

export const USER_PERMISSIONS_ENUM = {
  CAN_MANAGE_PAYMENTS: 'canManagePayments',
} as const;

export type UserPermissionEnum = typeof USER_PERMISSIONS_ENUM[keyof typeof USER_PERMISSIONS_ENUM];

export const PERMISSION_ROLE_TRANSLATION_KEYS = {
  canManagePayments: 'userPermissionLabels.canManagePayments',
} as const;

export type UserPermissionRoleEnum = typeof USER_PERMISSION_ROLE_ENUM[keyof typeof USER_PERMISSION_ROLE_ENUM];

export interface ApiUserPermissionsResponse {
  data: ApiPermission[];
}

export interface ApiPermission {
  role: UserPermissionRoleEnum;
  twrn: string;
  type: PermissionTypeEnum;
  can_manage_payments: boolean;
}

export class Permission {
  twrn: string;
  type: PermissionTypeEnum;
  role: UserPermissionRoleEnum;
  canManagePayments: boolean;

  constructor(data: ApiPermission) {
    this.twrn = data.twrn;
    this.type = data.type;
    this.role = data.role;
    this.canManagePayments = data.can_manage_payments;
  }
}

export interface ApiRoleCount {
  role: number;
  count: number;
}

export interface ApiPermissionCounts {
  can_manage_payments: number;
}
export class PermissionCounts {
  canManagePayments: number;

  constructor(data: ApiPermissionCounts) {
    this.canManagePayments = data.can_manage_payments;
  }
}

export interface ApiUserPermissionsListing {
  first_name: string;
  last_name: string;
  email: string;
  twrn: string;
  profile_photo_url: string | null;
  is_super_admin: boolean;
  role_counts: ApiRoleCount[];
  permission_counts: ApiPermissionCounts;
}

export interface ApiUserPermissionsListingResponse {
  data: ApiUserPermissionsListing[];
}

export class UserPermissionListing {
  firstName: string;
  lastName: string;
  email: string;
  twrn: string;
  avatar: string | null = null;
  isSuperAdmin: boolean;
  roleCounts: {
    role: number;
    count: number;
    translationKey: string;
  }[];

  permissionCounts: {
    [key: string]: {
      permission: string;
      count: number;
      translationKey: string;
    };
  };

  private _permissionCounts: PermissionCounts;

  constructor(data: ApiUserPermissionsListing) {
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.email = data.email;
    this.twrn = data.twrn;
    this.avatar = data.profile_photo_url;
    this.isSuperAdmin = data.is_super_admin;
    this._permissionCounts = new PermissionCounts(data.permission_counts);


    this.roleCounts = data.role_counts.map((role) => {
      const translationKey = USER_ROLE_TRANSLATION_KEYS[role.role as keyof typeof USER_ROLE_TRANSLATION_KEYS];
      return {
        role: role.role,
        count: role.count,
        translationKey,
      };
    }).sort((a, b) => {
      const isAViewer = a.role === USER_PERMISSION_ROLE_ENUM.VIEWER;
      const isBViewer = b.role === USER_PERMISSION_ROLE_ENUM.VIEWER;
      if (isAViewer && !isBViewer) return -1;
      if (!isAViewer && isBViewer) return 1;
      return 0;
    }); ;
    this.permissionCounts = Object.fromEntries(Object.entries(this._permissionCounts).map(([key, count]) => {
      const translationKey = PERMISSION_ROLE_TRANSLATION_KEYS[key as keyof typeof PERMISSION_ROLE_TRANSLATION_KEYS];
      return [key, {
        permission: key,
        count,
        translationKey,
      }];
    }));
  }

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }
}
